import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { ENTITY_TYPES } from "@/lib/salesConstants";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MODULES, enabledModules, moduleLabel } from "@/components/AppShell";
import { BrregNameSearch, BrregOrgField } from "@/components/BrregLookup";
import { ChartPlanPicker as SharedChartPlanPicker } from "@/components/husholdning/ChartPlanPicker";
import { entityToFields } from "@/lib/brreg";
import { CHART_PLANS, suggestedChartPlan as defaultChartPlan } from "@/lib/householdConstants";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Briefcase, Building2, User, Plus, Check, ArrowLeftRight, Pencil, Trash2, Loader2, Inbox, Receipt, KeyRound, ShieldCheck, BadgeCheck, MapPin, House, BookOpenText, AlertTriangle } from "lucide-react";

const MODULE_ICONS = { regninger: Inbox, faktura: Receipt, passord: KeyRound, husholdning: House };
const MODULE_HELP = {
  regninger: "Motta, tolke og holde oversikt over innkommende regninger.",
  faktura: "Send fakturaer, purringer og kreditnota med egen nummerserie.",
  passord: "Vis passordhvelvet i denne virksomheten. Hvelvet er felles for kontoen din – ett hovedpassord, ett hvelv.",
  husholdning: "Regninger som utgifter, utstedte fakturaer som inntekter og manuelle poster – ført etter kontoplanen du velger under.",
};

const EMPTY_FORM = { name: "", entity_type: "as", org_number: "", chart_plan: "ns4102_as", chart_plan_touched: false, modules: { regninger: true, faktura: true, passord: true, husholdning: true }, address: "", postal_code: "", city: "", country: "", website: "", mva_registered: null, foretaksregisteret: null, brreg_applied: "" };

/** Felter som kan forhåndsutfylle Firmaprofil ved opprettelse (typisk fra BRREG). */
const seedFields = (form) => ({
  address: form.address || null, postal_code: form.postal_code || null, city: form.city || null, country: form.country || null, website: form.website || null,
  mva_registered: form.mva_registered ?? null, foretaksregisteret: form.foretaksregisteret ?? null,
});

/** Bryter-rad for én modul. */
const ModuleToggle = ({ k, checked, onChange, testId, chartPlan }) => {
  const Icon = MODULE_ICONS[k];
  return (
    <label className={cn("flex items-start justify-between gap-3 rounded-lg border p-3 cursor-pointer transition-colors", checked ? "border-primary/40 bg-accent/30" : "hover:bg-secondary/60")}>
      <span className="flex items-start gap-2.5">
        <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", checked ? "text-primary" : "text-muted-foreground")} />
        <span>
          <span className="text-sm font-medium block">{moduleLabel(k, chartPlan)}</span>
          <span className="text-xs text-muted-foreground">{MODULE_HELP[k]}</span>
        </span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={moduleLabel(k, chartPlan)} data-testid={testId} />
    </label>
  );
};

/** Valg av kontoplan – delt komponent (brukes også i Regnskap-tomtilstanden). */
const ChartPlanPicker = ({ value, onChange, orgNumber, entityType, missingHint }) => <SharedChartPlanPicker value={value} onChange={onChange} orgNumber={orgNumber} entityType={entityType} idPrefix="chart-plan" missingHint={missingHint} testId="workspace-chart-plan" />;

/** Skjema for opprett/rediger virksomhet (navn, type, org.nr, moduler). */
const WorkspaceForm = ({ form, setForm, editing, idPrefix = "ws" }) => {
  const isCompany = form.entity_type !== "privat";
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  // Forhåndsvalgt kontoplan følger type/org.nr til brukeren velger selv; husholdning kan aldri stå igjen med org.nr
  useEffect(() => {
    setForm((f) => {
      const hasOrg = (f.org_number || "").replace(/\D/g, "").length > 0;
      const suggested = defaultChartPlan(f.entity_type, f.org_number);
      if (!f.chart_plan_touched || (f.chart_plan === "husholdning" && hasOrg)) {
        return f.chart_plan === suggested ? f : { ...f, chart_plan: suggested };
      }
      return f;
    });
  }, [form.entity_type, form.org_number, setForm]);

  /** Fyller inn fra BRREG: navn, org.nr, virksomhetstype (når kjent) og adresse som forhåndsutfyller Firmaprofil. */
  const applyBrreg = (entity) => {
    const b = entityToFields(entity);
    setForm((f) => ({
      ...f,
      name: b.name || f.name,
      org_number: b.org_number || f.org_number,
      entity_type: b.entity_type && ENTITY_TYPES[b.entity_type] && b.entity_type !== "annet" ? b.entity_type : f.entity_type,
      address: b.address, postal_code: b.postal_code, city: b.city, country: b.country || "Norge", website: b.website,
      mva_registered: entity?.vat_registered ?? null, foretaksregisteret: entity?.foretaksregisteret ?? null,
      brreg_applied: b.org_number,
    }));
    if (entity?.bankrupt || entity?.deleted_at) toast.warning(entity.deleted_at ? "Virksomheten er slettet i Enhetsregisteret" : "Virksomheten er registrert som konkurs");
  };
  return (
    <div className="space-y-4">
      {!editing ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-type`}>Virksomhetstype</Label>
          <Select value={form.entity_type} onValueChange={set("entity_type")}>
            <SelectTrigger id={`${idPrefix}-type`} data-testid="workspace-type-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card max-h-72">{Object.entries(ENTITY_TYPES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`workspace-type-${k}`}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ) : null}
      {!editing && isCompany ? (
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-org`}>Organisasjonsnummer</Label>
          <BrregOrgField id={`${idPrefix}-org`} value={form.org_number} onChange={(v) => setForm((f) => ({ ...f, org_number: v }))} onApply={applyBrreg} autoApply={!form.name.trim()} appliedOrgNumber={form.brreg_applied} testIdPrefix="workspace" />
        </div>
      ) : null}
      <div className="space-y-1.5">
        <Label htmlFor={`${idPrefix}-name`}>{isCompany ? "Navn på virksomheten" : "Navn"} *</Label>
        {!editing && isCompany ? (
          <BrregNameSearch id={`${idPrefix}-name`} value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} onSelect={applyBrreg} placeholder="Søk etter firma i Enhetsregisteret, eller skriv navnet" testIdPrefix="workspace" />
        ) : (
          <Input id={`${idPrefix}-name`} value={form.name} onChange={set("name")} placeholder={isCompany ? "f.eks. Nordmann Consulting AS" : "f.eks. Ola Nordmann"} data-testid="workspace-name-input" />
        )}
      </div>
      {!editing && isCompany && form.brreg_applied && (form.address || form.city) ? (
        <div className="rounded-lg border bg-secondary px-3 py-2 text-xs text-muted-foreground flex items-start gap-2" data-testid="workspace-brreg-address">
          <MapPin className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span><span className="font-medium text-foreground">Adresse fra BRREG:</span> {[form.address, [form.postal_code, form.city].filter(Boolean).join(" ")].filter(Boolean).join(", ")}. Legges inn i Firmaprofil sammen med {form.mva_registered ? "MVA-registrering" : "org.nr"} – du kan endre alt der.</span>
        </div>
      ) : !editing && isCompany ? (
        <p className="text-xs text-muted-foreground -mt-2">Org.nr kan fylles inn senere under Firmaprofil, men kreves før du kan utstede faktura.</p>
      ) : null}
      <ChartPlanPicker value={form.chart_plan} onChange={(v) => setForm((f) => ({ ...f, chart_plan: v, chart_plan_touched: true }))} orgNumber={form.org_number} entityType={form.entity_type} missingHint={editing && form.chart_plan_missing} />
      <div className="space-y-2">
        <Label>Moduler</Label>
        <div className="grid gap-2">
          {Object.keys(MODULES).map((k) => (
            <ModuleToggle key={k} k={k} checked={form.modules[k] !== false} onChange={(v) => setForm((f) => ({ ...f, modules: { ...f.modules, [k]: v } }))} testId={`workspace-module-${k}`} chartPlan={form.chart_plan} />
          ))}
        </div>
        {!Object.values(form.modules).some(Boolean) ? <p className="text-xs text-destructive" data-testid="workspace-modules-error">Minst én modul må være aktivert.</p> : null}
      </div>
    </div>
  );
};

export default function WorkspacesPage() {
  const { user, activeWorkspace, switchWorkspace, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [edit, setEdit] = useState(null); // workspace
  const [del, setDel] = useState(null); // workspace
  const [form, setForm] = useState(EMPTY_FORM);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState("");

  const load = async () => {
    setError("");
    try {
      const r = await api.get("/workspaces", { skipWorkspace: true });
      setItems(r.data.items);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY_FORM); setCreateOpen(true); };
  const openEdit = (w) => { setForm({ ...EMPTY_FORM, name: w.name, entity_type: w.entity_type, org_number: w.org_number || "", chart_plan: w.chart_plan || defaultChartPlan(w.entity_type, w.org_number), chart_plan_touched: true, chart_plan_missing: !w.chart_plan, modules: { ...w.modules } }); setEdit(w); };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !Object.values(form.modules).some(Boolean)) return;
    setBusy("create");
    try {
      const r = await api.post("/workspaces", { name: form.name.trim(), entity_type: form.entity_type, org_number: form.org_number || null, modules: form.modules, chart_plan: form.chart_plan, ...(form.brreg_applied ? seedFields(form) : {}) }, { skipWorkspace: true });
      toast.success(`${r.data.name} er opprettet`, { description: `Kontoplan: ${CHART_PLANS[r.data.chart_plan]?.label || r.data.chart_plan}. Bytt til virksomheten for å komme i gang.`, action: { label: "Bytt til", onClick: () => switchWorkspace(r.data.id, MODULES[enabledModules(r.data)[0]].home) } });
      setCreateOpen(false);
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!edit || !form.name.trim() || !Object.values(form.modules).some(Boolean)) return;
    setBusy("edit");
    try {
      await api.put(`/workspaces/${edit.id}`, { name: form.name.trim(), modules: form.modules, chart_plan: form.chart_plan }, { skipWorkspace: true });
      toast.success("Virksomheten er oppdatert");
      setEdit(null);
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  const submitDelete = async () => {
    if (!del) return;
    setBusy("delete");
    try {
      await api.delete(`/workspaces/${del.id}`, { data: { confirm_name: confirm }, skipWorkspace: true });
      toast.success(`${del.name} er slettet`);
      const wasActive = activeWorkspace?.id === del.id;
      setDel(null);
      setConfirm("");
      if (wasActive) { switchWorkspace(null, "/virksomheter"); return; }
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  if (error) return <Alert variant="destructive" data-testid="workspaces-error"><AlertTitle>Kunne ikke laste virksomheter</AlertTitle><AlertDescription>{error} <Button variant="link" className="h-auto p-0 ml-1" onClick={load}>Prøv igjen</Button></AlertDescription></Alert>;
  if (!items) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Konto"
        title="Virksomheter"
        description="Hver virksomhet – privatperson eller selskap – har egne regninger, egne kunder og fakturaer med egen nummerserie. Velg hvilke moduler hver virksomhet skal bruke."
        actions={<Button onClick={openCreate} data-testid="workspace-add-button"><Plus className="h-4 w-4 mr-2" /> Legg til virksomhet</Button>}
      />

      <ul className="grid gap-4 md:grid-cols-2" data-testid="workspaces-list">
        {items.map((w) => {
          const Icon = w.entity_type === "privat" ? User : Building2;
          const active = activeWorkspace?.id === w.id;
          const mods = enabledModules(w);
          return (
            <li key={w.id}>
              <Card className={cn("h-full", active && "border-primary ring-1 ring-primary/30")} data-testid={`workspace-card-${w.id}`}>
                <CardContent className="p-5 flex flex-col gap-4 h-full">
                  <div className="flex items-start gap-3">
                    <div className={cn("h-11 w-11 rounded-xl flex items-center justify-center shrink-0", active ? "bg-primary text-primary-foreground" : "bg-accent text-accent-foreground")}><Icon className="h-5 w-5" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-semibold truncate" data-testid={`workspace-name-${w.id}`}>{w.name || "Uten navn"}</h3>
                        {active ? <Badge className="rounded-full" data-testid={`workspace-active-badge-${w.id}`}><Check className="h-3 w-3 mr-1" /> Aktiv</Badge> : null}
                        {w.is_primary ? <Badge variant="outline" className="rounded-full">Primær</Badge> : null}
                      </div>
                      <p className="text-sm text-muted-foreground">{ENTITY_TYPES[w.entity_type] || w.entity_type}{w.org_number ? ` · org.nr ${w.org_number.replace(/(\d{3})(\d{3})(\d{3})/, "$1 $2 $3")}` : ""}</p>
                      {mods.includes("husholdning") ? (
                        w.chart_plan ? (
                          <p className="text-xs text-muted-foreground mt-0.5 inline-flex items-center gap-1" data-testid={`workspace-chart-plan-${w.id}`}><BookOpenText className="h-3 w-3" /> Kontoplan: {CHART_PLANS[w.chart_plan]?.label || w.chart_plan_label || w.chart_plan}</p>
                        ) : (
                          <button type="button" onClick={() => openEdit(w)} className="text-xs text-flag mt-0.5 inline-flex items-center gap-1 underline-offset-2 hover:underline" data-testid={`workspace-chart-plan-${w.id}`}><AlertTriangle className="h-3 w-3" /> Kontoplan ikke valgt – velg for å ta Regnskap i bruk</button>
                        )
                      ) : null}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5" data-testid={`workspace-modules-${w.id}`}>
                    {Object.keys(MODULES).map((k) => {
                      const MIcon = MODULE_ICONS[k];
                      const on = mods.includes(k);
                      return <span key={k} className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs", on ? "bg-accent text-accent-foreground border-transparent" : "text-muted-foreground line-through opacity-60")}><MIcon className="h-3 w-3" /> {moduleLabel(k, w.chart_plan)}</span>;
                    })}
                  </div>
                  <div className="mt-auto flex flex-wrap gap-2 pt-1">
                    {active ? (
                      <Button variant="outline" size="sm" onClick={() => navigate("/profil")} data-testid={`workspace-profile-${w.id}`}><BadgeCheck className="h-4 w-4 mr-1.5" /> {w.entity_type === "privat" ? "Min profil" : "Firmaprofil"}</Button>
                    ) : (
                      <Button size="sm" onClick={() => switchWorkspace(w.id, MODULES[mods[0] || "regninger"].home)} data-testid={`workspace-switch-${w.id}`}><ArrowLeftRight className="h-4 w-4 mr-1.5" /> Bytt til</Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEdit(w)} data-testid={`workspace-edit-${w.id}`}><Pencil className="h-4 w-4 mr-1.5" /> Rediger</Button>
                    {!w.is_primary ? <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive ml-auto" onClick={() => { setDel(w); setConfirm(""); }} data-testid={`workspace-delete-${w.id}`}><Trash2 className="h-4 w-4 mr-1.5" /> Slett</Button> : null}
                  </div>
                </CardContent>
              </Card>
            </li>
          );
        })}
      </ul>

      <Alert data-testid="workspaces-info">
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Slik fungerer virksomheter</AlertTitle>
        <AlertDescription>
          Alt du gjør i Controlle skjer i den aktive virksomheten – bytt øverst i toppen. Hver virksomhet har egen videresendingsadresse for regninger, egne kunder og egen fakturanummerserie. Passordhvelvet er felles for hele kontoen din (ett hovedpassord), og Familie og fullmakt gjelder din primære virksomhet ({items.find((w) => w.is_primary)?.name || user?.full_name}).
        </AlertDescription>
      </Alert>

      {/* Opprett */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="workspace-create-dialog">
          <form onSubmit={submitCreate} className="space-y-5" noValidate>
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Briefcase className="h-5 w-5 text-primary" /> Ny virksomhet</DialogTitle><DialogDescription>Opprett en privatperson eller et selskap med egne data. Du kan bytte mellom virksomhetene når som helst.</DialogDescription></DialogHeader>
            <WorkspaceForm form={form} setForm={setForm} editing={false} idPrefix="ws-new" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} data-testid="workspace-create-cancel">Avbryt</Button>
              <Button type="submit" disabled={busy === "create" || !form.name.trim() || !Object.values(form.modules).some(Boolean)} data-testid="workspace-create-submit">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Opprett virksomhet</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Rediger */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="bg-card sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="workspace-edit-dialog">
          <form onSubmit={submitEdit} className="space-y-5" noValidate>
            <DialogHeader><DialogTitle>Rediger {edit?.name}</DialogTitle><DialogDescription>Navn, kontoplan og moduler. Virksomhetstype, organisasjonsnummer og adresse endrer du under {edit?.entity_type === "privat" ? "Min profil" : "Firmaprofil"} når virksomheten er aktiv.</DialogDescription></DialogHeader>
            <WorkspaceForm form={form} setForm={setForm} editing idPrefix="ws-edit" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEdit(null)} data-testid="workspace-edit-cancel">Avbryt</Button>
              <Button type="submit" disabled={busy === "edit" || !form.name.trim() || !Object.values(form.modules).some(Boolean)} data-testid="workspace-edit-submit">{busy === "edit" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />} Lagre</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Slett */}
      <Dialog open={!!del} onOpenChange={(o) => { if (!o) { setDel(null); setConfirm(""); } }}>
        <DialogContent className="bg-card sm:max-w-md" data-testid="workspace-delete-dialog">
          <DialogHeader><DialogTitle className="text-destructive">Slette {del?.name}?</DialogTitle><DialogDescription>Alle regninger, leverandører, kunder, fakturaer og dokumenter i denne virksomheten slettes permanent. Passordhvelvet ditt påvirkes ikke. Dette kan ikke angres.</DialogDescription></DialogHeader>
          <div className="space-y-1.5 py-2"><Label htmlFor="ws-del-confirm">Skriv navnet «{del?.name}» for å bekrefte</Label><Input id="ws-del-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="off" data-testid="workspace-delete-confirm-input" /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDel(null)} data-testid="workspace-delete-cancel">Avbryt</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={busy === "delete" || confirm.trim().toLowerCase() !== (del?.name || "").trim().toLowerCase()} data-testid="workspace-delete-submit">{busy === "delete" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />} Slett virksomheten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
